import React, { useState, useEffect, useRef } from 'react';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useOptionalNavigation } from '../contexts/NavigationContext';
import { DataService } from '../services/dataService';
import { ContextualChatButton } from '../components/ContextualChatButton';
import { ExerciseCard, BaseExercise } from '../components/ExerciseCard';
import { UnifiedWorkoutTimer } from '../components/UnifiedWorkoutTimer';
import { WorkoutSessionResults } from '../components/WorkoutSessionResults';
import { WorkoutHistoryDisplay } from '../components/WorkoutHistoryDisplay';
import { useProfile } from '../contexts/ProfileContext';
import { DataSourceBadges } from '../components/badges/DataSourceBadges';
import { storage } from '../services/storage';
import { CmpBootomnavCoach as ProperNavCoach } from '../anima/Navigation Component/src/components/CmpBootomnavCoach/CmpBootomnavCoach.jsx';

interface MobilityDetailProps {
  routineId?: string; // Optional since we'll get from URL params
}

// Enhanced mobility type display with professional styling and evidence-based benefits
const MOBILITY_TYPE_CONFIG = {
  smr: { 
    label: 'Self-Myofascial Release', 
    color: 'bg-blue-100 text-blue-800',
    icon: '🎯',
    benefits: ['Releases trigger points', 'Improves tissue quality', 'Reduces muscle tension'],
    breathingPattern: 'Deep, slow breaths during pressure application',
    contraindications: ['Acute injuries', 'Recent surgery', 'Blood clotting disorders']
  },
  art: { 
    label: 'Active Release Technique', 
    color: 'bg-green-100 text-green-800',
    icon: '🔄',
    benefits: ['Breaks up scar tissue', 'Improves range of motion', 'Restores muscle function'],
    breathingPattern: 'Coordinated breathing with movement patterns',
    contraindications: ['Acute inflammation', 'Open wounds', 'Severe pain']
  },
  'joint-mob': { 
    label: 'Joint Mobilization', 
    color: 'bg-purple-100 text-purple-800',
    icon: '🦴',
    benefits: ['Improves joint ROM', 'Reduces stiffness', 'Enhances synovial fluid production'],
    breathingPattern: 'Gentle exhale during mobilization',
    contraindications: ['Joint instability', 'Acute inflammation', 'Fractures']
  },
  dynamic: { 
    label: 'Dynamic Stretching', 
    color: 'bg-orange-100 text-orange-800',
    icon: '⚡',
    benefits: ['Prepares muscles for activity', 'Improves blood flow', 'Enhances neuromuscular control'],
    breathingPattern: 'Natural rhythm matching movement tempo',
    contraindications: ['Cold muscles', 'Recent injury', 'Extreme fatigue']
  },
  static: { 
    label: 'Static Stretching', 
    color: 'bg-indigo-100 text-indigo-800',
    icon: '🧘',
    benefits: ['Increases flexibility', 'Reduces muscle tension', 'Promotes relaxation'],
    breathingPattern: 'Deep diaphragmatic breathing throughout hold',
    contraindications: ['Before explosive activities', 'Acute muscle strain', 'Hypermobility']
  },
  yoga: { 
    label: 'Yoga/Mind-body', 
    color: 'bg-pink-100 text-pink-800',
    icon: '🕉️',
    benefits: ['Integrates mind-body awareness', 'Improves balance', 'Reduces stress hormones'],
    breathingPattern: 'Ujjayi breathing or specific pranayama techniques',
    contraindications: ['Severe balance issues', 'Recent head injury', 'Uncontrolled blood pressure']
  },
  corrective: { 
    label: 'Corrective Exercise', 
    color: 'bg-yellow-100 text-yellow-800',
    icon: '⚖️',
    benefits: ['Addresses muscle imbalances', 'Improves movement patterns', 'Prevents injury'],
    breathingPattern: 'Controlled breathing coordinated with activation patterns',
    contraindications: ['Acute pain', 'Recent surgery', 'Inability to maintain proper form']
  },
  warmup: { 
    label: 'Pre-exercise Prep', 
    color: 'bg-red-100 text-red-800',
    icon: '🔥',
    benefits: ['Increases core temperature', 'Prepares nervous system', 'Reduces injury risk'],
    breathingPattern: 'Gradually increasing respiratory rate',
    contraindications: ['Illness with fever', 'Extreme fatigue', 'Recent injury to target area']
  },
  cooldown: { 
    label: 'Post-exercise Recovery', 
    color: 'bg-cyan-100 text-cyan-800',
    icon: '❄️',
    benefits: ['Promotes recovery', 'Reduces metabolic byproducts', 'Calms nervous system'],
    breathingPattern: 'Progressive slowing of breath rate',
    contraindications: ['Dizziness', 'Chest pain', 'Severe dehydration']
  }
};

/**
 * Outer shell: resolves the routine id and guards on it. It deliberately owns
 * no state and no effects.
 *
 * 2026-09-17 (hooks debt): the guard used to sit INSIDE the component, above
 * eleven useState/useRef calls and two effects — so the Mar-18 note ("ALL hooks
 * MUST be declared before any conditional returns") described four hooks and
 * was wrong about the other fourteen. `routineId` can be missing on one render
 * and present on the next, and a component that renders N hooks once and N+14
 * the next makes React throw "Rendered more hooks than during the previous
 * render". Splitting the guard from the hooks removes the class: every hook in
 * MobilityDetailLoaded runs on every render of MobilityDetailLoaded.
 */
export const MobilityDetail: React.FC<MobilityDetailProps> = (props) => {
  // 🐛 FEB 3, 2026 (Bug #73 - Timer Crash Fix): Get routineId from URL params
  const params = useLocalSearchParams<{ id: string; source?: string }>();
  const routineId = props.routineId || params.id;

  // Skip render if no routineId
  if (!routineId) {
    console.error('❌ MobilityDetail: No routineId found in props or URL params');
    return null;
  }

  return <MobilityDetailLoaded routineId={routineId} />;
};

const MobilityDetailLoaded: React.FC<{ routineId: string }> = ({ routineId }) => {
  // `params.source` is read further down (pre-/post-workout launch context).
  const params = useLocalSearchParams<{ id: string; source?: string }>();

  const router = useRouter();
  const { profile } = useProfile();

  // 🔧 FIX JAN 29: Defensive navigation hook usage
  // 2026-09-17: was `useNavigation?.() ?? null` — a hook behind an optional
  // call is a Rules-of-Hooks violation, and it still THREW when no provider was
  // mounted. useOptionalNavigation() is called unconditionally and returns null.
  const navigationContext = useOptionalNavigation();

  const goBack = navigationContext?.goBack || (() => router.back());
  const getCurrentSource = navigationContext?.getCurrentSource || (() => 'unknown');

  const routine = DataService.getMobilityRoutineById(routineId);
  const [expandedExercise, setExpandedExercise] = useState<string | null>(null);
  const [detailLevel, setDetailLevel] = useState<'simple' | 'advanced'>('simple');
  const [isCompletedToday, setIsCompletedToday] = useState(false);
  
  // 🆕 JAN 2026 PRIORITY 3: Detect if this is component mobility (warmup/cooldown)
  const isComponentMobility = routine?.context === 'component' && routine?.workoutId;
  const parentWorkout = isComponentMobility ? DataService.getWorkoutById(routine.workoutId) : undefined;
  
  // Session tracking state
  const [showUnifiedTimer, setShowUnifiedTimer] = useState(false);
  const [sessionData, setSessionData] = useState<any>(null);
  const [showSessionResults, setShowSessionResults] = useState(false);
  
  // 🆕 PHASE 3: Mobility timer state (Jan 22, 2026)
  const [timerStarted, setTimerStarted] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // 🔄 JAN 21, 2026: Daily routine state (Phase 8)
  const isDailyRoutine = routine?.frequency === 'daily' && routine?.context === 'daily';
  const [dailyCompletions, setDailyCompletions] = useState<{
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  }>({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false
  });
  const [adherenceStats, setAdherenceStats] = useState<{
    total_days: number;
    completed_days: number;
    adherence_percentage: number;
    weeks_analyzed: number;
  } | null>(null);
  
  // 🆕 PHASE 3: Detect warmup/cooldown reference mode (Jan 22, 2026)
  const title = routine?.title?.toLowerCase() || '';
  const isWarmupMode = isComponentMobility &&
                       params.source === 'pre-workout' &&
                       (title.includes('warm') || title.includes('pre-'));

  const isCooldownMode = isComponentMobility &&
                         params.source === 'post-workout' &&
                         (title.includes('cool') || title.includes('post-') || 
                          title.includes('stretch') || title.includes('recovery') || 
                          title.includes('foam'));
  
  const isReferenceMode = isWarmupMode || isCooldownMode;
  
  // 🆕 PHASE 3: Timer effect (Jan 22, 2026)
  useEffect(() => {
    if (timerStarted && !isPaused) {
      timerIntervalRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
    
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [timerStarted, isPaused]);
  
  // 🆕 PHASE 3: Auto-complete when timer finishes (Jan 22, 2026)
  useEffect(() => {
    if (!timerStarted || !routine) return;
    
    // Parse duration (e.g., "8 min" → 480 seconds)
    const durationMatch = routine.duration?.match(/(\d+)\s*min/);
    const targetSeconds = durationMatch ? parseInt(durationMatch[1]) * 60 : 0;
    
    if (targetSeconds > 0 && elapsedSeconds >= targetSeconds) {
      // Timer finished!
      console.log(`⏱️ TIMER COMPLETE: ${isWarmupMode ? 'Warmup' : 'Cooldown'} finished`);
      
      if (isWarmupMode) {
        // WARMUP: Automatic transition to main workout timer
        console.log('🏃 AUTO-TRANSITION: Navigating to main workout timer');
        router.push(`/workout-detail/${routine.workoutId}?source=warmup-complete`);
      } else if (isCooldownMode) {
        // COOLDOWN: Auto-complete and navigate back
        console.log('✅ AUTO-COMPLETE: Marking cooldown complete and navigating back');
        handleCompleteCooldown();
      }
    }
  }, [elapsedSeconds, timerStarted, routine, isWarmupMode, isCooldownMode, router]);
  
  // 🆕 PHASE 3: Timer handlers (Jan 22, 2026)
  const handleBeginTimer = () => {
    console.log(`🎬 BEGIN: Starting ${isWarmupMode ? 'warmup' : 'cooldown'} timer`);
    setTimerStarted(true);
    setIsPaused(false);
  };
  
  const handlePauseTimer = () => {
    setIsPaused(!isPaused);
  };
  
  const handleResetTimer = () => {
    setElapsedSeconds(0);
    setIsPaused(false);
  };
  
  // 🔧 FIX FEB 4, 2026: Use NavigationContext goBack() ONLY for stateful overlay
  const handleSmartBack = () => {
    console.log('🔙 MobilityDetail: handleSmartBack called');
    const source = getCurrentSource();
    console.log('🔙 MobilityDetail: source =', source, 'isComponentMobility =', isComponentMobility);
    
    // For component mobility, special handling (route-based, not overlay)
    if (isComponentMobility && parentWorkout) {
      console.log('🔙 MobilityDetail: Component mobility - using route navigation');
      // If came from workout, go back to workout
      if (source === 'workout-detail') {
        console.log('🔙 MobilityDetail: Going back to workout detail:', parentWorkout.id);
        router.push(`/workout/${parentWorkout.id}`);
        return;
      }
      // If came from plan, go back to plan
      if (source === 'plan-detail' && routine?.planId) {
        console.log('🔙 MobilityDetail: Going back to plan detail:', routine.planId);
        router.push(`/plan/${routine.planId}`);
        return;
      }
    }
    
    // 🔧 CRITICAL: MyPath uses stateful overlay pattern (line 1978)
    // Must use goBack() to reset navigationState, NOT navigate()
    if (navigationContext) {
      console.log('🔙 MobilityDetail: Using NavigationContext.goBack() for stateful overlay');
      
      // Dispatch event to MyPath to set viewMode/entityFilter
      if (source === 'today' || source === 'browse-sessions' || source === 'browse-plans' || source === 'browse-goals') {
        console.log('🔙 MobilityDetail: Dispatching navigateBackToMyPath event for source:', source);
        window.dispatchEvent(new CustomEvent('navigateBackToMyPath', { 
          detail: { source } 
        }));
      }
      
      // Reset NavigationContext state (closes detail overlay)
      navigationContext.goBack();
      console.log('🔙 MobilityDetail: NavigationContext.goBack() called - detail should close');
    } else {
      console.log('🔙 MobilityDetail: No NavigationContext - falling back to router.back()');
      // Fallback for route-based navigation
      router.back();
    }
  };

  // Session handlers
  const handleStartSession = () => {
    setShowUnifiedTimer(true);
  };

  const handleSessionComplete = async (workoutSessionData: any) => {
    console.log('Mobility session completed:', workoutSessionData);
    setSessionData(workoutSessionData);
    setShowUnifiedTimer(false);
    setShowSessionResults(true);
    
    // 🔥 MOBILITY FIX: Mark mobility complete and persist session data
    if (routine) {
      const result = DataService.markMobilityComplete(routine.id);
      if (result.success) {
        // Update local state to show completion
        setIsCompletedToday(true);
        
        // 🗄️ PERSIST MOBILITY SESSION DATA
        const sessionRecord = {
          id: workoutSessionData.id,
          mobilityId: routine.id,
          mobilityTitle: routine.title,
          sessionType: 'mobility',
          sessionData: workoutSessionData,
          completedAt: new Date().toISOString(),
          activityType: 'mobility'
        };
        
        // Store session for historical analysis
        const existingSessions = JSON.parse(await storage.getItem('workoutSessions') || '[]');
        existingSessions.push(sessionRecord);
        await storage.setItem('workoutSessions', JSON.stringify(existingSessions));
        
        // 🎯 CEDAR PROGRESS ANALYSIS: Analyze mobility improvement
        const progressAnalysis = DataService.analyzeActivityProgress(routine.id);
        
        if (progressAnalysis.shouldCelebrate && progressAnalysis.progressInsights.length > 0) {
          // Show progress celebration in session results
          setSessionData(prev => ({
            ...prev,
            progressInsights: progressAnalysis.progressInsights,
            shouldCelebrate: true,
            comparisonData: progressAnalysis.comparisonData
          }));
          
          // Log progress for Cedar chat context
          console.log('🎉 MOBILITY PROGRESS DETECTED:', progressAnalysis.progressInsights.join(' '));
        }
      }
    }
  };

  const handleSessionDiscard = () => {
    setShowUnifiedTimer(false);
  };

  const handleSessionExit = () => {
    setShowUnifiedTimer(false);
  };

  const handleCloseResults = () => {
    setShowSessionResults(false);
    setSessionData(null);
  };

  const handleMarkIncomplete = () => {
    setIsCompletedToday(false);
    // In real app, this would also clear the completion status from backend
    console.log('✅ Mobility routine marked as incomplete');
  };

  // 🆕 PHASE 4.3: Complete cooldown handler (component mobility)
  const handleCompleteCooldown = async () => {
    if (!routine) return;
    
    try {
      // Mark cooldown complete via DataService
      const result = DataService.markMobilityComplete(routine.id);
      if (result.success) {
        setIsCompletedToday(true);
        console.log('✅ Component cooldown completed:', routine.title);
        
        // Store actual duration
        const sessionRecord = {
          id: `cooldown_${Date.now()}`,
          mobilityId: routine.id,
          mobilityTitle: routine.title,
          sessionType: 'component_cooldown',
          completedAt: new Date().toISOString(),
          parentWorkoutId: routine.workoutId,
          activityType: 'mobility'
        };
        
        const existingSessions = JSON.parse(await storage.getItem('workoutSessions') || '[]');
        existingSessions.push(sessionRecord);
        await storage.setItem('workoutSessions', JSON.stringify(existingSessions));
        
        // Navigate back after brief delay
        setTimeout(() => handleSmartBack(), 500);
      }
    } catch (error) {
      console.error('❌ Error completing cooldown:', error);
    }
  };

  if (!routine) {
    return (
      <div className="flex flex-col h-full bg-white">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <button 
            onClick={handleSmartBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-lg font-semibold">Routine Not Found</h1>
          <div className="w-6 h-6" />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Routine Not Found</h2>
            <p className="text-gray-600 mb-4">The requested mobility routine could not be found.</p>
            <button 
              onClick={handleSmartBack}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Convert poses to BaseExercise format for consistency with workout
  const exercises: BaseExercise[] = routine.poses?.map(pose => ({
    id: pose.id,
    name: pose.name,
    description: pose.description,
    type: 'pose' as const,
    duration: pose.duration,
    repetitions: pose.repetitions,
    instructions: pose.instructions,
    benefits: pose.benefits,
    // 🐛 BUG #74 FIX: Handle modifications as string or array (backend generates strings)
    modifications: typeof pose.modifications === 'string' 
      ? [pose.modifications] // Wrap string in array
      : (Array.isArray(pose.modifications) ? pose.modifications : []), // Use array or empty
    precautions: pose.precautions,
    images: pose.images,
    videos: pose.videos,
    equipment: routine.equipment || []
  })) || [];

  const handleExerciseToggle = (exerciseId: string) => {
    setExpandedExercise(expandedExercise === exerciseId ? null : exerciseId);
  };

  // Get type configuration from modality with smart mapping
  // Database stores modality ('stretch', 'yoga', 'physio')
  // Config uses technique types ('static', 'dynamic', 'yoga', etc.)
  const modalityToType: Record<string, string> = {
    'stretch': 'static',       // Default stretch → static stretching
    'yoga': 'yoga',            // Yoga stays yoga
    'physio': 'corrective',    // Physio → corrective exercise
    'static-stretching': 'static',
    'dynamic-stretching': 'dynamic'
  };
  
  const lookupKey = routine.modality ? modalityToType[routine.modality] || routine.modality : 'static';
  const typeConfig = MOBILITY_TYPE_CONFIG[lookupKey] || {
    label: routine.modality || 'Mobility',
    color: 'bg-gray-100 text-gray-800',
    icon: '🔄'
  };

  const handleCompletionToggle = () => {
    setIsCompletedToday(!isCompletedToday);
    // In real app, this would save to backend
  };

  // 🆕 PHASE 3: Format elapsed time as MM:SS (Jan 22, 2026)
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // 🆕 PHASE 3: Calculate target duration in seconds (Jan 22, 2026)
  const getTargetSeconds = () => {
    const durationMatch = routine?.duration?.match(/(\d+)\s*min/);
    return durationMatch ? parseInt(durationMatch[1]) * 60 : 0;
  };

  // 🏠 HOME BUTTON PATTERN (Jan 22, 2026): Handle bottom navigation
  // When Plan tab clicked from mobility detail, always navigate to Today view
  const handleNavigation = (tab: string) => {
    switch (tab) {
      case 'cedar':
        router.push('/chat');
        break;
      case 'my-path':
      case 'mypath':
        router.push('/mypath?resetToToday=true');
        break;
      case 'momentum':
      case 'insights':
        router.push('/insights');
        break;
      case 'settings':
        router.push('/settings');
        break;
      default:
        router.push('/chat');
        break;
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-white max-w-md mx-auto flex flex-col">
      {/* 🔧 JAN 22, 2026: Simplified Header - Back button and title only */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 z-10 bg-white flex-shrink-0"
           style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}>
        <button 
          onClick={handleSmartBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-lg font-semibold">{routine.title}</h1>
        {/* 🔧 JAN 22, 2026: Removed Chat button from header */}
        <div className="w-6" /> {/* Spacer for layout balance */}
      </div>

      {/* 🆕 JAN 22, 2026: Scrollable content area with padding for fixed action button + nav */}
      <div 
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={{ 
          paddingBottom: 'calc(152px + env(safe-area-inset-bottom, 0px))', // 70px action button + 82px nav
          WebkitOverflowScrolling: 'touch'
        }}
      >

      {/* 🆕 PHASE 3: Reference Mode Layout (Warmup/Cooldown) - Jan 22, 2026 */}
      {isReferenceMode ? (
        <div className="p-4 space-y-4">
            
            {/* Parent Workout Badge */}
            {parentWorkout && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <div>
                    <p className="text-xs text-blue-600 font-medium">
                      {isWarmupMode ? '🔥 Pre-Workout Warmup' : '❄️ Post-Workout Cooldown'}
                    </p>
                    <p className="text-sm font-semibold text-blue-900">{parentWorkout.title}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* 🔧 JAN 22, 2026: Timer button moved to fixed position above navigation */}
            {!timerStarted && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-3 shadow-md">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-green-900 mb-1">
                  {isWarmupMode ? 'Ready to Warm Up?' : 'Ready to Cool Down?'}
                </h3>
                <p className="text-green-800 text-sm mb-1">
                  {routine.duration} • {exercises.length} poses
                </p>
                <p className="text-green-700 text-xs">
                  ⏱️ Use the button below to start
                </p>
              </div>
            )}
            
            {/* Timer Display (if started) */}
            {timerStarted && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl font-bold text-blue-900">
                      {formatTime(elapsedSeconds)}
                    </div>
                    <div className="text-sm text-blue-700">
                      <div className="font-medium">
                        {isWarmupMode ? '🔥 Warming Up' : '❄️ Cooling Down'}
                      </div>
                      <div className="text-xs">
                        Target: {routine.duration}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={handlePauseTimer}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                        isPaused 
                          ? 'bg-green-600 text-white hover:bg-green-700' 
                          : 'bg-gray-600 text-white hover:bg-gray-700'
                      }`}
                    >
                      {isPaused ? '▶️ Resume' : '⏸️ Pause'}
                    </button>
                    <button
                      onClick={handleResetTimer}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium text-sm transition-colors"
                    >
                      🔄 Reset
                    </button>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="mt-3">
                  <div className="w-full bg-blue-100 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${Math.min((elapsedSeconds / getTargetSeconds()) * 100, 100)}%` 
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* All Poses Visible (Reference Layout) */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {exercises.length} Poses
              </h2>
              
              {exercises.map((exercise, index) => (
                <div key={exercise.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {/* Pose Header - 🐛 BUG #74 FIX: Add image to header like WorkoutDetail exercise cards */}
                  <button
                    onClick={() => handleExerciseToggle(exercise.id)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-semibold text-sm flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="text-left flex-1">
                        <h3 className="font-semibold text-gray-900">{exercise.name}</h3>
                        {exercise.duration && (
                          <p className="text-sm text-gray-600">{exercise.duration}</p>
                        )}
                      </div>
                      {/* 🐛 BUG #74 FIX: Thumbnail image on right (20x20 standard, clickable for full view) */}
                      {exercise.images && exercise.images.length > 0 ? (
                        <div 
                          className="w-20 h-20 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent double-expansion
                            handleExerciseToggle(exercise.id);
                          }}
                        >
                          <img 
                            src={exercise.images[0]} 
                            alt={exercise.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback to numbered circle if image fails (safe DOM manipulation)
                              const target = e.currentTarget;
                              const parent = target.parentElement;
                              if (parent) {
                                while (parent.firstChild) parent.removeChild(parent.firstChild);
                                const outer = document.createElement('div');
                                outer.className = 'w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center';
                                const inner = document.createElement('div');
                                inner.className = 'w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center';
                                const span = document.createElement('span');
                                span.className = 'text-sm font-bold text-blue-700';
                                span.textContent = String(index + 1);
                                inner.appendChild(span);
                                outer.appendChild(inner);
                                parent.appendChild(outer);
                              }
                            }}
                          />
                        </div>
                      ) : (
                        // No image: Show numbered circle (matches ExerciseCard fallback)
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <div className="w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-blue-700">{index + 1}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <svg 
                      className={`w-5 h-5 text-gray-400 transition-transform ml-2 flex-shrink-0 ${
                        expandedExercise === exercise.id ? 'rotate-180' : ''
                      }`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Expanded Content */}
                  {expandedExercise === exercise.id && (
                    <div className="p-4 pt-0 space-y-3 border-t border-gray-100">
                      {/* Image */}
                      {exercise.images && exercise.images.length > 0 && (
                        <img 
                          src={exercise.images[0]} 
                          alt={exercise.name}
                          className="w-full h-48 object-cover rounded-lg"
                        />
                      )}
                      
                      {/* Instructions */}
                      {exercise.instructions && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">Instructions</h4>
                          <p className="text-sm text-gray-700">{exercise.instructions}</p>
                        </div>
                      )}
                      
                      {/* Benefits */}
                      {exercise.benefits && exercise.benefits.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">Benefits</h4>
                          <ul className="text-sm text-gray-700 space-y-1">
                            {exercise.benefits.map((benefit, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-green-500 mt-0.5">•</span>
                                <span>{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Modifications */}
                      {exercise.modifications && exercise.modifications.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">Modifications</h4>
                          <ul className="text-sm text-gray-700 space-y-1">
                            {exercise.modifications.map((mod, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5">→</span>
                                <span>{mod}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                ))}
            </div>
          </div>
      ) : (
        /* NORMAL MODE (Standalone Mobility) - Original content */
        <div className="p-4 space-y-6">
          
          {/* 🆕 PHASE 4.2: Parent Workout Badge (component mobility only) */}
          {isComponentMobility && parentWorkout && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <div>
                  <p className="text-xs text-blue-600 font-medium">Part of Workout</p>
                  <p className="text-sm font-semibold text-blue-900">{parentWorkout.title}</p>
                </div>
              </div>
            </div>
          )}

          
          {/* Mobility Type Tag - Like workout tags */}
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${typeConfig.color}`}>
              {typeConfig.icon} {typeConfig.label}
            </span>
            {routine.difficulty && (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                {routine.difficulty}
              </span>
            )}
          </div>

          {/* Hero Section with Completion Status */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <p className="text-gray-700 text-sm leading-relaxed">{routine.description}</p>
              </div>
              
              {/* Daily Completion Toggle */}
              <button
                onClick={handleCompletionToggle}
                className={`ml-4 p-3 rounded-full transition-all duration-200 ${
                  isCompletedToday 
                    ? 'bg-white text-yellow-500' 
                    : 'bg-yellow-500 text-white hover:bg-yellow-600'
                }`}
                title={isCompletedToday ? 'Completed today! Click for tomorrow' : 'Mark as done for today'}
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L19 5.5V4L17 4V6.5L19 8V10L21 9ZM3 9L5 10V8L7 6.5V4L5 4V5.5L3 7V9ZM12 20C10.9 20 10 19.1 10 18C10 16.9 10.9 16 12 16C13.1 16 14 16.9 14 18C14 19.1 13.1 20 12 20ZM8.5 17.5L7.5 16.5L6.5 17.5L8.5 19.5L8.5 17.5ZM16.5 17.5L15.5 16.5L16.5 17.5L17.5 16.5L16.5 17.5Z"/>
                </svg>
              </button>
            </div>

            {/* Duration & Equipment Info */}
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {routine.duration}
              </span>
              {routine.equipment && routine.equipment.length > 0 && (
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  {routine.equipment.join(', ')}
                </span>
              )}
            </div>
          </div>

          {/* View Toggle - Simple vs Advanced */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDetailLevel('simple')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                detailLevel === 'simple'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Simple
            </button>
            <button
              onClick={() => setDetailLevel('advanced')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                detailLevel === 'advanced'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Advanced
            </button>
          </div>

          {/* Mobility Session History */}
          <WorkoutHistoryDisplay mobilityId={routine.id} maxSessions={3} />

          {/* 🆕 PHASE 4.2: Different buttons based on context */}
          {(() => {
            // 🆕 JAN 5, 2026: Enhanced debugging for button visibility
            console.log('🔘 Mobility Button Check:', {
              routineId: routine?.id,
              isCompletedToday,
              isComponentMobility,
              shouldShowButton: !isCompletedToday,
              routine: routine ? 'exists' : 'missing'
            });
            
            return !isCompletedToday;
          })() && (
            <div className="mb-6">
              {/* 🔧 JAN 22, 2026: Action buttons moved to fixed position above navigation */}
              {/* Info card about the session */}
              {isComponentMobility ? (
                <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-lg p-4">
                  <div className="flex flex-col gap-2">
                    <h4 className="text-sm font-semibold text-teal-900">Ready to Complete?</h4>
                    <p className="text-teal-800 text-sm">
                      Review the exercises below, then use the button at the bottom to mark complete.
                    </p>
                  </div>
                </div>
              ) : (
                /* Standalone Mobility: Info about timer */
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
                  <div>
                    <h4 className="text-sm font-semibold text-purple-900 mb-1">Ready to Start?</h4>
                    <p className="text-purple-800 text-sm">
                      Use the button at the bottom to begin your guided mobility session.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Completed Mobility Options - Show if completed */}
          {isCompletedToday && (
            <div className="mb-6">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-green-900 mb-1">Mobility Completed!</h4>
                      <p className="text-green-800 text-sm">
                        Great job completing this mobility routine!
                      </p>
                    </div>
                  </div>
                  {(() => {
                    const sessionHistory = DataService.getMobilitySessionHistory(routine.id);
                    const hasSessionData = sessionHistory.length > 0;

                    return !hasSessionData ? (
                      <button
                        onClick={handleMarkIncomplete}
                        className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors font-medium text-xs"
                        title="Mark as incomplete (no session data to lose)"
                      >
                        Uncheck
                      </button>
                    ) : null;
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* 🌲 Cedar Insights — Phase 3A: Show for ALL mobility types (standalone + daily) */}
          {isCompletedToday && routine?.coaching_notes && (
            <div className="mb-6">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-green-900 mb-3 flex items-center gap-2">
                  <span>🌲</span> Cedar Insights
                </h4>
                <ul className="space-y-2">
                  {(() => {
                    const notes = routine.coaching_notes;
                    const insights: string[] = [];
                    if (typeof notes === 'string') {
                      insights.push(notes);
                    } else if (typeof notes === 'object' && notes !== null) {
                      if (notes.what) insights.push(notes.what);
                      if (notes.why) insights.push(notes.why);
                      if (notes.guidance) insights.push(notes.guidance);
                    }
                    return insights.map((insight, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-green-800">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                        <span>{insight}</span>
                      </li>
                    ));
                  })()}
                </ul>
                {/* DataSourceBadges — show data source attribution post-completion */}
                {routine.data_sources && routine.data_sources.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <DataSourceBadges sources={routine.data_sources} size="small" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 🔄 JAN 21, 2026: Daily Routine Completion Section (Phase 8) */}
          {isDailyRoutine && (
            <div className="mb-8">
              <div 
                className="bg-white rounded-lg border-2 border-dashed p-6"
                style={{ 
                  borderColor: 'rgba(182, 122, 52, 0.4)',
                  background: 'linear-gradient(to bottom, rgba(254, 249, 195, 0.1), white)'
                }}
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <span>📊</span> Track Your Daily Practice
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Check off each day you complete this routine. Consistency compounds!
                </p>
                
                {/* Week checkbox grid */}
                <div className="grid grid-cols-7 gap-3 mb-6">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((dayLabel, idx) => {
                    const dayNames: Array<keyof typeof dailyCompletions> = [
                      'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
                    ];
                    const dayName = dayNames[idx];
                    const completed = dailyCompletions[dayName];
                    
                    // Calculate date for this day
                    const getDateForDay = (): string => {
                      if (!routine?.scheduledDate) return '';
                      const planStart = new Date(routine.scheduledDate);
                      const targetDate = new Date(planStart);
                      targetDate.setDate(planStart.getDate() + idx);
                      return targetDate.toISOString().split('T')[0];
                    };
                    
                    const date = getDateForDay();
                    
                    return (
                      <button
                        key={idx}
                        onClick={async () => {
                          if (!date) return;
                          
                          try {
                            const result = await DataService.toggleDailyRoutineCompletion(routine.id, date);
                            
                            // Update local state
                            setDailyCompletions(prev => ({
                              ...prev,
                              [dayName]: result.completed
                            }));
                            
                            // Reload adherence stats
                            const stats = await DataService.getDailyRoutineAdherenceStats(routine.id, 4);
                            setAdherenceStats(stats);
                          } catch (error) {
                            console.error('Failed to toggle completion:', error);
                          }
                        }}
                        className="flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all"
                        style={{
                          borderColor: completed ? 'var(--color-success)' : 'var(--cedar-limestone)',
                          background: completed ? 'rgba(34, 197, 94, 0.1)' : 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                          style={{
                            background: completed ? 'var(--color-success)' : 'var(--cedar-limestone)',
                            color: completed ? 'white' : 'var(--coloron-surface600)'
                          }}
                        >
                          {completed ? '✓' : dayLabel[0]}
                        </div>
                        <span className="text-xs font-medium" style={{ color: 'var(--coloron-surface700)' }}>
                          {dayLabel}
                        </span>
                      </button>
                    );
                  })}
                </div>
                
                {/* Adherence stats */}
                {adherenceStats && (
                  <div 
                    className="bg-green-50 rounded-lg p-4 border border-green-200"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-900 font-semibold text-base">
                          {adherenceStats.adherence_percentage.toFixed(0)}% Adherence
                        </p>
                        <p className="text-green-700 text-sm">
                          {adherenceStats.completed_days} of {adherenceStats.total_days} days completed (last {adherenceStats.weeks_analyzed} weeks)
                        </p>
                      </div>
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl"
                        style={{ background: 'var(--color-success)' }}
                      >
                        {adherenceStats.adherence_percentage.toFixed(0)}%
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Poses/Exercises Section - Consistent with workout layout */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {detailLevel === 'simple' ? 'Poses' : 'Detailed Poses'}
              </h2>
            </div>
            
            {exercises.length > 0 ? (
              <div className="space-y-0">
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-purple-900 mb-1">Mobility Routine</h4>
                  <p className="text-purple-800 text-sm">
                    This routine includes <span className="font-medium">{exercises.length} mobility poses</span>. 
                    Each includes breathing patterns, modifications, and safety notes.
                    {profile?.gender && (
                      <span className="block mt-1 text-xs">
                        Media demonstrates {profile.gender === 'male' ? 'male' : profile.gender === 'female' ? 'female' : 'alternating'} demonstrators for better relatability.
                      </span>
                    )}
                  </p>
                </div>
                
                {/* Exercise List using Universal Component - Same as workout */}
                <div className="space-y-0">
                  {exercises.map((exercise, index) => (
                    <ExerciseCard
                      key={exercise.id}
                      exercise={exercise}
                      index={index}
                      expandedId={expandedExercise}
                      onToggleExpansion={handleExerciseToggle}
                      variant="mobility"
                    />
                  ))}
                </div>
              </div>
            ) : (
              // Clean fallback for basic routines
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${typeConfig.color.replace('text-', 'bg-').replace('-800', '-100')}`}>
                    <div className={typeConfig.color}>
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Begin</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    This {routine.duration} {typeConfig.label.toLowerCase()} routine is ready for you to start.
                  </p>
                  <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {routine.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      {routine.difficulty || 'Beginner'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Advanced Details Tab */}
          {detailLevel === 'advanced' && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Advanced Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Benefits */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Key Benefits</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {((typeConfig as any)?.benefits)?.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-green-500 mt-1">•</span>
                        <span>{benefit}</span>
                      </li>
                    )) || []}
                  </ul>
                </div>

                {/* Breathing Pattern */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Breathing Pattern</h4>
                  <p className="text-sm text-gray-600">{((typeConfig as any)?.breathingPattern)}</p>
                </div>

                {/* Contraindications */}
                <div className="md:col-span-2">
                  <h4 className="font-medium text-gray-900 mb-2">Contraindications</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {((typeConfig as any)?.contraindications)?.map((contraindication, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-red-500 mt-1">⚠</span>
                        <span>{contraindication}</span>
                      </li>
                    )) || []}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {/* End conditional: isReferenceMode ? ReferenceLayout : NormalLayout */}

      </div> {/* End scrollable content */}

      {/* 🆕 JAN 22, 2026: Fixed Action Button - Just above navigation */}
      {/* 🔄 JAN 21, 2026: Hide button for daily routines (use checkbox grid instead) */}
      {!timerStarted && !isCompletedToday && !isDailyRoutine && (
        <div 
          className="action-button-container fixed left-1/2 transform -translate-x-1/2 bg-white px-4 pt-3 pb-0"
          style={{ 
            bottom: 'calc(82px + env(safe-area-inset-bottom, 0px))', // Butts up against nav - no gap
            width: '100%',
            maxWidth: '448px',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
            zIndex: 40
          }}
        >
          <button
            onClick={isReferenceMode 
              ? (isCooldownMode ? handleCompleteCooldown : handleBeginTimer) // 🐛 BUG #74 FIX: Check cooldown mode, not just isComponentMobility
              : handleStartSession
            }
            className="w-full bg-[#1F6C4F] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#1F4524] transition-colors flex items-center justify-center gap-2 shadow-lg"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {isReferenceMode 
              ? (isCooldownMode ? 'Complete Cooldown' : `Begin ${isWarmupMode ? 'Warmup' : 'Cooldown'}`) // 🐛 BUG #74 FIX: Proper mode-specific text
              : 'Start Session'
            }
          </button>
        </div>
      )}

      {/* 🆕 JAN 22, 2026: Fixed Bottom Navigation - matches WorkoutDetail and MyPath */}
      <div 
        className="navigation-container fixed bottom-0 left-1/2 transform -translate-x-1/2 bg-white border-t border-gray-200 z-50"
        style={{ width: '100%', maxWidth: '448px' }}
      >
        <ProperNavCoach 
          activeTab="mypath"
          className=""
          onClickTab={handleNavigation}
        />
      </div>

      {/* Session Modal (outside conditional - works for both modes) */}
      {showUnifiedTimer && (
        <UnifiedWorkoutTimer
          workoutId={routine.id}
          workoutTitle={routine.title}
          workoutType="mobility"
          exercises={exercises.map(ex => ({
            id: ex.id,
            name: ex.name,
            description: ex.description,
            duration: ex.duration,
            instructions: ex.instructions,
            images: ex.images
          }))}
          onComplete={handleSessionComplete}
          onDiscard={handleSessionDiscard}
          onExit={handleSessionExit}
        />
      )}

      {showSessionResults && (
        <WorkoutSessionResults
          workoutData={sessionData}
          workoutTitle={routine.title}
          onClose={handleCloseResults}
        />
      )}
    </div>
  );
}; 